import { useRef, useState } from 'react'
import type { ComputationResult } from '../api'

/**
 * Single-row classification chip: step name + short value + confidence
 * + stale flag, all on one line. Hover or click reveals a popup with
 * the full reason, all extras (legal areas, key phrases, linked matter
 * names, deadlines, etc.), prompt version + latency + timestamp, and
 * the raw JSON in a collapsible.
 */

type ClassificationView = {
  headline: string
  /** Hex/CSS class hint. Empty string = neutral. */
  tone: '' | 'pos' | 'neg' | 'urgent' | 'warn' | 'info'
  confidence?: number
  reason?: string
  extra?: Record<string, unknown>
}

const TONE_CLASSES: Record<ClassificationView['tone'], string> = {
  '': 'me-text',
  pos: 'text-[color:var(--me-fresh)]',
  neg: 'text-[color:var(--me-warn)]',
  urgent: 'text-[color:var(--me-urgent,var(--me-warn))] font-medium',
  warn: 'text-[color:var(--me-stale,var(--me-warn))]',
  info: 'me-text',
}

export function classifyOutput(stepName: string, raw: unknown): ClassificationView {
  const o = (raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {})
  const s = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : undefined)
  const n = (k: string) => (typeof o[k] === 'number' ? (o[k] as number) : undefined)
  const b = (k: string) => (typeof o[k] === 'boolean' ? (o[k] as boolean) : undefined)
  switch (stepName) {
    case 'is_personal': {
      const v = b('is_personal')
      return {
        headline: v == null ? '?' : (v ? 'yes' : 'no'),
        tone: v == null ? '' : (v ? 'warn' : 'pos'),
        confidence: n('confidence'),
        reason: s('reason'),
      }
    }
    case 'detect_doc_kind': {
      return {
        headline: s('doc_kind') ?? '?',
        tone: 'info',
        confidence: n('confidence'),
        reason: s('rationale'),
      }
    }
    case 'detect_urgency': {
      const u = n('urgency')
      return {
        headline: u == null ? '?' : `${u}/5`,
        tone: u == null ? '' : (u >= 4 ? 'urgent' : u >= 3 ? 'warn' : 'info'),
        reason: s('reason'),
        extra: s('deadline_mentioned') ? { deadline: s('deadline_mentioned') } : undefined,
      }
    }
    case 'sensitive_content_check': {
      const v = b('is_sensitive')
      return {
        headline: v ? 'sensitive' : 'not sensitive',
        tone: v ? 'warn' : 'pos',
        reason: s('reason'),
        extra: {
          category: s('category'),
          restrict_to_recipient: b('restrict_to_recipient'),
        },
      }
    }
    case 'summarize_artifact': {
      const summary = s('summary') ?? ''
      return {
        headline: summary ? 'complete' : '—',
        tone: summary ? 'info' : '',
        reason: summary,
        extra: Array.isArray(o.key_phrases) ? { key_phrases: o.key_phrases } : undefined,
      }
    }
    case 'extract_parties': {
      const parties = Array.isArray(o.parties) ? (o.parties as Array<{ name?: string }>) : []
      return {
        headline: `${parties.length} part${parties.length === 1 ? 'y' : 'ies'}`,
        tone: 'info',
        reason: parties.map((p) => p?.name).filter(Boolean).slice(0, 6).join(', '),
        extra: { count: parties.length },
      }
    }
    case 'extract_subject_matter': {
      const topic = s('primary_topic') ?? ''
      return {
        headline: topic ? 'complete' : '—',
        tone: topic ? 'info' : '',
        reason: topic,
        extra: {
          legal_areas: Array.isArray(o.legal_areas) ? (o.legal_areas as string[]).join(', ') : '',
          match_keywords: o.match_keywords,
        },
      }
    }
    case 'aggregate_match_and_decide': {
      const action = s('action') ?? '?'
      const links = Array.isArray(o.links) ? (o.links as Array<{ matter_name?: string }>) : []
      const tone: ClassificationView['tone'] =
        action === 'file_existing' || action === 'file_existing_and_alert'
          ? 'pos'
          : action === 'skip'
            ? ''
            : action === 'create_new'
              ? 'info'
              : 'warn'
      const SHORT: Record<string, string> = {
        file_existing: 'existing',
        file_existing_and_alert: 'existing+alert',
        create_new: 'new',
        skip: 'skip',
        hold_for_review: 'hold',
        request_clarification: 'clarify',
      }
      return {
        headline: SHORT[action] || action.replaceAll('_', ' '),
        tone,
        reason: s('reasoning'),
        extra: {
          links: links.map((l) => l?.matter_name).filter(Boolean),
          proposed_new_matters: o.proposed_new_matters,
        },
      }
    }
    default: {
      const keys = Object.keys(o).filter((k) => !['fake', 'model'].includes(k))
      if (!keys.length) return { headline: '—', tone: '' }
      const first = keys[0]
      const v = o[first]
      return {
        headline: `${first}: ${typeof v === 'string' ? v.slice(0, 30) : JSON.stringify(v).slice(0, 30)}`,
        tone: '',
      }
    }
  }
}

const POPUP_WIDTH = 420
const POPUP_GAP = 4   // px gap between card edge and popup edge

export default function ClassificationCard({ r }: { r: ComputationResult }) {
  const view = classifyOutput(r.step_name, r.output)
  const [showDetails, setShowDetails] = useState(false)
  // We render the popup with position:fixed using viewport coords so
  // it escapes any ancestor's overflow:auto / overflow:hidden — which
  // is what was clipping it inside the artifact-detail pane.
  const [popupCoords, setPopupCoords] = useState<{ left: number; top: number } | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const stepLabel = r.step_name.replaceAll('_', ' ')

  const open = () => {
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    const spaceRight = window.innerWidth - rect.right
    // Side picked based on which has more room: prefer growing right
    // (popup left edge under card's left), fall back to growing left.
    const popupLeft =
      spaceRight >= POPUP_WIDTH + POPUP_GAP
        ? rect.left
        : Math.max(8, rect.right - POPUP_WIDTH)
    const popupTop = rect.bottom + POPUP_GAP
    // Clamp top so popup doesn't fall off the bottom of the viewport.
    const maxTop = window.innerHeight - 80  // need at least some room
    setPopupCoords({ left: popupLeft, top: Math.min(popupTop, maxTop) })
    setShowDetails(true)
  }

  return (
    <div
      ref={cardRef}
      className="me-card relative px-2 py-1.5"
      onMouseEnter={open}
      onMouseLeave={() => setShowDetails(false)}
      onClick={() => (showDetails ? setShowDetails(false) : open())}
    >
      <div className="flex items-baseline gap-2 text-[12px] leading-snug">
        <span className="me-mono text-[11px] me-text-dim truncate min-w-0 flex-1">
          {stepLabel}
        </span>
        <span className={`shrink-0 font-medium ${TONE_CLASSES[view.tone]}`}>
          {view.headline}
        </span>
        {typeof view.confidence === 'number' && (
          <span className="me-mono shrink-0 text-[10px] me-text-mute">
            {Math.round(view.confidence * 100)}%
          </span>
        )}
        {r.is_stale && <span className="me-chip me-chip-stale shrink-0 self-center">stale</span>}
        {r.status === 'failed' && <span className="me-chip me-chip-urgent shrink-0 self-center">failed</span>}
      </div>

      {showDetails && popupCoords && (
        <div
          className="overflow-hidden rounded-sm border me-border bg-[color:var(--me-surface)] p-3 text-[12px] shadow-lg"
          style={{
            position: 'fixed',
            left: popupCoords.left,
            top: popupCoords.top,
            width: POPUP_WIDTH,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100vh - 16px)',
            overflowY: 'auto',
            zIndex: 60,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
          onMouseEnter={() => setShowDetails(true)}
          onMouseLeave={() => setShowDetails(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="me-section-h !mb-1">{stepLabel}</div>
          <div className={`mb-1 break-words ${TONE_CLASSES[view.tone]}`}>
            <span className="font-medium">{view.headline}</span>
            {typeof view.confidence === 'number' && (
              <span className="me-mono ml-1.5 text-[10px] me-text-mute">
                ({Math.round(view.confidence * 100)}% confidence)
              </span>
            )}
          </div>
          {view.reason && (
            <div className="mt-1 break-words text-[12px] me-text-dim leading-snug">
              {view.reason}
            </div>
          )}
          {view.extra && Object.keys(view.extra).length > 0 && (
            <div className="mt-2 me-mono text-[10px] me-text-mute">
              {Object.entries(view.extra)
                .filter(([, v]) => v !== undefined && v !== null && v !== '')
                .map(([k, v]) => (
                  <div key={k} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    <span className="me-text-dim">{k}:</span>{' '}
                    {typeof v === 'string' ? v : JSON.stringify(v)}
                  </div>
                ))}
            </div>
          )}
          <div className="mt-2 me-mono text-[10px] me-text-mute">
            v{r.prompt_version_number} · {r.latency_ms}ms ·{' '}
            {new Date(r.computed_at).toLocaleString()}
            {r.is_stale && ' · stale'}
            {r.status === 'failed' && ' · failed'}
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer me-mono text-[10px] me-text-mute hover:underline">
              raw JSON
            </summary>
            <pre
              className="me-mono mt-1 max-h-[200px] overflow-auto rounded-sm bg-[color:var(--me-bg)] p-1.5 text-[10px] leading-relaxed"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            >
              {JSON.stringify(r.output, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}
